import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay } from 'rxjs';
import { Card, ExplanationBlock } from './data.service';

// ── Raw bilingual JSON shape ──────────────────────────────────────────────

export interface BilingualRow {
  row_id: string;
  english: any | null;
  hindi: any | null;
  alignment_method: string;
  confidence: number | null;
  low_confidence: boolean;
  missing: 'english' | 'hindi' | null;
}

export interface BilingualTopic {
  chunk_index: number;
  topic_id: string | null;
  section_id: string | null;
  section_eng: string | null;   // e.g. "Indian Geography"
  section_hin: string | null;   // e.g. "भारतीय भूगोल"
  topic_eng: string | null;
  topic_hin: string | null;
  missing_side: string | null;
  english_count: number;
  hindi_count: number;
  alignment_method: string;
  rows: BilingualRow[];
}

export interface BilingualDoc {
  subject_name?: string;
  subject_name_hin?: string;
  english_total: number;
  hindi_total: number;
  paired: number;
  english_only?: number;
  hindi_only?: number;
  topics: BilingualTopic[];
}

// ── Nav tree types ────────────────────────────────────────────────────────

export interface NavLeaf {
  label: string;
  labelHin?: string;
  selected: boolean;
  chunkIndex: number;
  topicId: string | null;
}
export interface NavSection {
  label: string;
  expanded: boolean;
  children: NavLeaf[];
}
export interface NavSubject {
  label: string;
  expanded: boolean;
  sections: NavSection[];
}

// ─────────────────────────────────────────────────────────────────────────

const EXAM_YEAR_MAP: { pattern: RegExp; field: string }[] = [
  { pattern: /I\.A\.S\.|UPSC|Civil Services/i,          field: 'yearIAS'   },
  { pattern: /U\.P\.|UPPSC|U\.P\.R\.O\.|U\.P\.P\.C\.S\./i, field: 'yearUPPSC' },
  { pattern: /B\.P\.S\.C\.|BPSC|Bihar/i,                field: 'yearBPSC'  },
  { pattern: /M\.P\.|MPPCS|M\.P\.P\.C\.S\./i,           field: 'yearMPPSC' },
  { pattern: /R\.A\.S\.|R\.T\.S\.|RPSC|Rajasthan/i,     field: 'yearRPSC'  },
  { pattern: /Uttarakhand|UKPSC/i,                       field: 'yearUKPSC' },
  { pattern: /Chhattisgarh|CGPSC/i,                      field: 'yearCGPSC' },
  { pattern: /Jharkhand|JPSC|J\.P\.C\.S\./i,            field: 'yearJPSC'  },
];

@Injectable({ providedIn: 'root' })
export class BilingualJsonLoaderService {
  private readonly ASSET_URL  = 'assets/ocr-questions/_bilingual_questions.json';
  private readonly FIGURE_BASE = 'assets/ocr-questions/figures/';

  private cached$: Observable<BilingualDoc> | null = null;

  constructor(private http: HttpClient) {}

  load(): Observable<BilingualDoc> {
    if (!this.cached$) {
      this.cached$ = this.http.get<BilingualDoc>(this.ASSET_URL).pipe(shareReplay(1));
    }
    return this.cached$;
  }

  // ── Nav tree ────────────────────────────────────────────────────────────

  buildNavTree(doc: BilingualDoc): NavSubject[] {
    const subjectLabel = doc.subject_name || doc.subject_name_hin || 'Content';

    // Group chunks by section_eng (human-readable), preserving insertion order
    const sectionOrder: string[] = [];
    const sectionMap = new Map<string, BilingualTopic[]>();
    for (const t of doc.topics) {
      const key = t.section_eng || t.section_id || 'General';
      if (!sectionMap.has(key)) {
        sectionOrder.push(key);
        sectionMap.set(key, []);
      }
      sectionMap.get(key)!.push(t);
    }

    const sections: NavSection[] = sectionOrder.map(key => {
      const chunks = sectionMap.get(key)!;
      const children: NavLeaf[] = chunks.map(chunk => ({
        label: this.chunkLabel(chunk),
        labelHin: chunk.topic_hin ?? undefined,
        chunkIndex: chunk.chunk_index,
        topicId: chunk.topic_id,
        selected: false,
      }));
      return { label: key, expanded: true, children };
    });

    return [{ label: subjectLabel, expanded: true, sections }];
  }

  private chunkLabel(chunk: BilingualTopic): string {
    if (chunk.topic_eng) return chunk.topic_eng;
    for (const row of chunk.rows) {
      if (row.english?.topic) return row.english.topic;
    }
    return chunk.topic_hin ?? `Topic ${chunk.chunk_index}`;
  }

  // ── Cards for one chunk ─────────────────────────────────────────────────

  getCardsForChunk(chunk: BilingualTopic): Card[] {
    return chunk.rows.map(row => this.rowToCard(row));
  }

  private rowToCard(row: BilingualRow): Card {
    const eng = row.english;
    const hin = row.hindi;
    const primary = eng || hin;

    const engOpts: Record<string, string> = eng?.options ?? {};
    const hinOpts: Record<string, string> = hin?.options ?? {};
    const answer = (eng?.answer ?? hin?.answer ?? '').toLowerCase();
    const answerText = answer
      ? `(${answer}) ${engOpts[answer] || hinOpts[answer] || ''}`
      : '';

    const examName: string = eng?.exam_name ?? hin?.exam_name ?? '';
    const examYear: string = String(eng?.exam_year ?? hin?.exam_year ?? '');
    const examLabel = examYear && examName
      ? `${examName} ${examYear}`
      : examName || examYear;

    const yearFields: Partial<Card> = {};
    for (const m of EXAM_YEAR_MAP) {
      if (m.pattern.test(examName)) {
        (yearFields as any)[m.field] = examYear;
        break;
      }
    }

    // Question figure from English question_blocks
    let questionFigureUrl = '';
    let questionFigureCaption = '';
    const qBlocks: any[] = eng?.question_blocks ?? [];
    const qFigure = qBlocks.find((b: any) => b.type === 'figure');
    if (qFigure?.image_ref) {
      questionFigureUrl = this.FIGURE_BASE + this.extractFilename(qFigure.image_ref);
      questionFigureCaption = qFigure.content ?? '';
    }

    // Explanation blocks (English)
    const explanationBlocks: ExplanationBlock[] = (eng?.explanation_blocks ?? []).map((b: any) => ({
      type: b.type as 'text' | 'table' | 'figure',
      content: b.content ?? '',
      headers: b.headers ?? [],
      rows: b.rows ?? [],
      imageUrl: b.type === 'figure' && b.image_ref
        ? this.FIGURE_BASE + this.extractFilename(b.image_ref)
        : undefined,
    }));

    return {
      id: primary?.global_question_number ?? 0,
      questionText: eng?.question_text ?? '',
      hindiQuestionText: hin?.question_text ?? '',
      imageUrl: '',
      answerText,
      answerImageUrl: '',
      explanationText: eng?.explanation ?? '',
      hindiExplanationText: hin?.explanation ?? '',
      explanationImageUrl: '',
      collapsed: false,
      questionNumber: eng?.question_number ?? hin?.question_number,
      optionAText: engOpts['a'] ?? '',
      optionBText: engOpts['b'] ?? '',
      optionCText: engOpts['c'] ?? '',
      optionDText: engOpts['d'] ?? '',
      hindiOptionAText: hinOpts['a'] ?? '',
      hindiOptionBText: hinOpts['b'] ?? '',
      hindiOptionCText: hinOpts['c'] ?? '',
      hindiOptionDText: hinOpts['d'] ?? '',
      missingHindi: row.missing === 'hindi',
      missingEnglish: row.missing === 'english',
      rowId: row.row_id,
      topicId: (eng?.topic_id ?? hin?.topic_id) ?? undefined,
      questionFigureUrl,
      questionFigureCaption,
      explanationBlocks,
      examLabel,
      ...yearFields,
    };
  }

  private extractFilename(absPath: string): string {
    if (!absPath) return '';
    return absPath.replace(/\\/g, '/').split('/').pop() ?? '';
  }
}
